import type { Identifier } from "ra-core";

import type {
  AdvisorUnavailability,
  LeadCarouselNotice,
  OnGuardiaNowEntry,
  ProjectGuardiaRosterRow,
  ProjectOfficeHours,
  ReassignLeadFromNoticeParams,
  ReassignLeadResult,
  ReassignProtectedLeadParams,
  SetCanReassignProtectedLeadsParams,
} from "../../types";
import { mexicoCityMoment } from "../fakerest/carouselShared";
import { computeOnGuardiaNow } from "../fakerest/guardiaRules";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EL2 (TASK-073): typed reads of the BPO carousel section. RLS (TASK-070) decides
 * what each caller sees: members of a project read its guardias and office hours, editors
 * (the Team Leader of that project, direccion, a bpo lead_manager) read and write, everyone
 * else gets zero rows. The tables and the `project_guardias_roster` view are also reachable
 * through the generic data provider (`getList("project_guardias", ...)`, with `id`), so
 * writes (guardias, office hours, 'no disponible' ranges, the 'Activa' flag) use its generic
 * create / update. The follow-up evaluator and the outbox are service_role only: no client
 * method exists for them. Nothing here returns attendance, counts or rankings.
 */
const fail = (error: { message: string } | null, fallback: string) => {
  if (error) throw new Error(error.message || fallback);
};

/** One project-week of the guardia roster (the view, names included), oldest first. */
const getProjectGuardiaRoster = async (
  projectId: Identifier,
  dateFrom?: string,
  dateTo?: string,
): Promise<ProjectGuardiaRosterRow[]> => {
  let query = getSupabaseClient()
    .from("project_guardias_roster")
    .select("*")
    .eq("project_id", projectId);
  if (dateFrom) query = query.gte("guardia_date", dateFrom);
  if (dateTo) query = query.lte("guardia_date", dateTo);
  const { data, error } = await query.order("guardia_date").order("start_time");
  fail(error, "No se pudo leer el rol de guardias");
  return (data ?? []) as ProjectGuardiaRosterRow[];
};

const getProjectOfficeHours = async (
  projectId: Identifier,
): Promise<ProjectOfficeHours[]> => {
  const { data, error } = await getSupabaseClient()
    .from("project_office_hours")
    .select("*")
    .eq("project_id", projectId)
    .order("weekday");
  fail(error, "No se pudo leer el horario de atención");
  return (data ?? []) as ProjectOfficeHours[];
};

export const createSupabaseCarouselMethods = () => ({
  getProjectGuardiaRoster,
  getProjectOfficeHours,

  /** Active (not cancelled) 'no disponible' ranges; one advisor, or every one RLS shows. */
  async getAdvisorUnavailability(
    salesId?: Identifier,
  ): Promise<AdvisorUnavailability[]> {
    let query = getSupabaseClient()
      .from("advisor_unavailability")
      .select("*")
      .is("cancelled_at", null);
    if (salesId != null) query = query.eq("sales_id", salesId);
    const { data, error } = await query.order("starts_at");
    fail(error, "No se pudo leer la disponibilidad");
    return (data ?? []) as AdvisorUnavailability[];
  },

  /** The carousel notices of a project (the notified advisor and the editors read them). */
  async getLeadCarouselNotices(
    projectId: Identifier,
  ): Promise<LeadCarouselNotice[]> {
    const { data, error } = await getSupabaseClient()
      .from("lead_carousel_notices")
      .select("*")
      .eq("project_id", projectId)
      .order("id", { ascending: false });
    fail(error, "No se pudieron leer los avisos");
    return (data ?? []) as LeadCarouselNotice[];
  },

  /**
   * Who is on guardia now: derived from today's roster and the office hours only; no presence
   * or attendance data exists.
   */
  async getProjectOnGuardiaNow(
    projectId: Identifier,
  ): Promise<OnGuardiaNowEntry[]> {
    const now = new Date();
    const today = mexicoCityMoment(now).date;
    const [roster, hours] = await Promise.all([
      getProjectGuardiaRoster(projectId, today, today),
      getProjectOfficeHours(projectId),
    ]);
    return computeOnGuardiaNow(roster, hours, projectId, now);
  },

  /**
   * Almikia Epic EL3: Direccion reassigns a proactivo / colega lead. Thin wrapper over the
   * SECURITY DEFINER `reassign_protected_lead()` RPC, which checks the per-person permission
   * `sales.can_reassign_protected_leads`, the mandatory reason and the target, and writes the
   * history row. `error.message` is the Spanish text the function raises, forwarded as-is.
   */
  async reassignProtectedLead({
    dealId,
    newSalesId,
    reason,
  }: ReassignProtectedLeadParams): Promise<ReassignLeadResult> {
    const { data, error } = await getSupabaseClient().rpc(
      "reassign_protected_lead",
      { p_deal_id: dealId, p_new_sales_id: newSalesId, p_reason: reason },
    );
    if (error || !data) {
      throw new Error(error?.message || "No se pudo reasignar el lead");
    }
    return data as ReassignLeadResult;
  },

  /**
   * Almikia Epic EL3: the Team Leader reassigns a lead FROM A NOTICE (cadence alert or any
   * carousel notice), with a mandatory reason. Wrapper over `reassign_lead_from_notice()`.
   */
  async reassignLeadFromNotice({
    noticeId,
    newSalesId,
    reason,
  }: ReassignLeadFromNoticeParams): Promise<ReassignLeadResult> {
    const { data, error } = await getSupabaseClient().rpc(
      "reassign_lead_from_notice",
      { p_notice_id: noticeId, p_new_sales_id: newSalesId, p_reason: reason },
    );
    if (error || !data) {
      throw new Error(error?.message || "No se pudo reasignar el lead");
    }
    return data as ReassignLeadResult;
  },

  /**
   * Almikia Epic EL3: the Direccion-only switch for `sales.can_reassign_protected_leads`.
   * Wrapper over the SECURITY DEFINER `set_can_reassign_protected_leads()` RPC, which refuses
   * everyone but direccion and her own profile. `error.message` is the Spanish text it raises.
   */
  async setCanReassignProtectedLeads({
    salesId,
    value,
  }: SetCanReassignProtectedLeadsParams): Promise<boolean> {
    const { data, error } = await getSupabaseClient().rpc(
      "set_can_reassign_protected_leads",
      { p_sales_id: salesId, p_value: value },
    );
    if (error || typeof data !== "boolean") {
      throw new Error(error?.message || "No se pudo cambiar el permiso");
    }
    return data;
  },
});
